import React, { useMemo, useState } from 'react';
import globalProgramStore from '../../../../stores/program-store';
import globalCourseStore from '../../../../stores/global-course-store';
import { useNavigate } from 'react-router-dom';
import AppApi from '../../../../api/app-api';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMeta,
  DataTableRowClickEvent,
  DataTableSelectionMultipleChangeEvent,
} from 'primereact/datatable';
import { CourseMetadataType } from '../../../../api/types/programs/course/course-metadata.type';
import ExportButton from '../../../../component/export-button/export-button';
import { CourseStatus } from '../../../../api/types/programs/course/course-status';
import { observer } from 'mobx-react-lite';
import { FilterMatchMode } from 'primereact/api';
import { Tag } from 'primereact/tag';
import '../../../../component/styles/react-prime-custom-syle.css';
import { Dropdown } from 'primereact/dropdown';
import Colors from '../../../../utils/colors';
import './asd.css';
import { Button } from 'primereact/button';
import {
  getHebrewYear,
  numberToHebrewLetter,
} from '../../../../utils/hebrew-date-utils';
import { updateCourseYearOperation } from './operations/update-course-year-operation';
import { deleteProgramCourseOperation } from './operations/delete-program-course-operation';
import globalConfigStore from '../../../../stores/global-config-store';
import { ProgramYearsDistribution } from '../../../../api/types/programs/program.type';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';
import globalSettingsBranches from '../../../../stores/global-settings-branches';

interface ExtendedCourseMetadata
  extends Omit<CourseMetadataType, 'programYear'> {
  isVirtual?: boolean;
  programYear: number | null;
}

type VirtualYearRow = {
  programYear: number;
  id: string;
  isVirtual: boolean;
};

type TableRowType = ExtendedCourseMetadata | VirtualYearRow;

const ProgramCoursesTab: React.FC = observer(() => {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<TableRowType[]>([]);
  const [expandedRows, setExpandedRows] = useState<TableRowType[]>([]);

  const currentProgramId = globalProgramStore.getCurrentId()!;
  const rawProgramData = globalProgramStore.programs.get(currentProgramId);
  const programData = rawProgramData as unknown as ProgramYearsDistribution;

  // Combine all courses from all years and unassigned courses
  const allCourses = useMemo<ExtendedCourseMetadata[]>(() => {
    if (!programData?.details?.years) return [];

    const yearCourses = programData.details.years.flatMap(
      (yearData) =>
        yearData.courses.map((course) => ({
          ...course,
          programYear: yearData.year,
        })) as ExtendedCourseMetadata[],
    );

    const unassignedCourses = (programData.details?.unAssignCourses || []).map(
      (course) => ({
        ...course,
        programYear: null,
      }),
    ) as ExtendedCourseMetadata[];

    return [...yearCourses, ...unassignedCourses].sort((a, b) => {
      if (a.programYear === null) return -1;
      if (b.programYear === null) return 1;
      return a.programYear - b.programYear;
    });
  }, [programData]);

  // Get total years from program metadata
  const totalYears = programData?.metadata?.years || 0;
  const existingYears = new Set(allCourses.map((course) => course.programYear));

  // Create an array of years we want to ensure exist
  const desiredYears = Array.from({ length: totalYears }, (_, i) => i);

  // Create virtual courses only for years that don't already exist in your data
  const missingYearRows = desiredYears
    .filter((year) => !existingYears.has(year))
    .map((year) => ({
      programYear: year,
      id: `virtual-${year}`,
      isVirtual: true,
    }));

  // Combine real and virtual courses
  const coursesWithVirtualGroups = [...allCourses, ...missingYearRows];

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'abstractData.courseName': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    'abstractData.courseHours': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },

    capacityHours: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    'abstractData.attendancePercentage': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    status: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    programYear: {
      value: null,
      matchMode: FilterMatchMode.EQUALS,
    },
  });

  const onRowSelected = async (
    index: number,

    courseMetadata: CourseMetadataType,
  ) => {
    if (
      courseMetadata.programYear === undefined ||
      courseMetadata.programYear === null
    ) {
      return;
    }
    console.log(
      `Course selected with index ${index} and courseMetadata: ${courseMetadata}`,
    );
    const programId = globalProgramStore.getCurrentId();
    if (
      courseMetadata.id &&
      programId /* &&
      !(courseMetadata.status === CourseStatus.INIT)*/
    ) {
      // TODO Rest api call
      const res = await AppApi.getCourseDetails(programId, courseMetadata.id);
      globalCourseStore.addCourseMetadata(res.data.body.metadata);
      globalCourseStore.addCourseDetails(
        courseMetadata.id,
        res.data.body.details,
      );
      globalCourseStore.currentId = courseMetadata.id;
      const path = `/campuses/${rawProgramData.metadata.branchId}/programs/${globalProgramStore.getCurrentId()}/courses/${courseMetadata.id}`;
      navigate(path);
    }
  };

  function createFilterProps(filterPlaceholder: string) {
    return {
      filter: true,
      filterPlaceholder: filterPlaceholder,
      filterMatchMode: 'contains',
      showFilterMenu: false,
    };
  }

  const getCourseStatus = (status: CourseStatus) => {
    switch (status) {
      case CourseStatus.ACTIVE:
        return <Tag value={'פעיל'} severity={'info'} />;
      case CourseStatus.CLOSED:
        return <Tag value={'נסגר'} severity={'warning'} />;
      case CourseStatus.FINISHED:
        return <Tag value={'הסתיים'} severity={'success'} />;
      case CourseStatus.INIT:
        return (
          <Tag
            value={'אותחל'}
            severity={'secondary'}
            className={'neutral-tag'}
          />
        );
    }
  };

  const statusFilterElement = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={[
          CourseStatus.ACTIVE,
          CourseStatus.CLOSED,
          CourseStatus.FINISHED,
          CourseStatus.INIT,
        ]}
        onChange={(e) => options.filterApplyCallback(e.value)}
        valueTemplate={getCourseStatus}
        itemTemplate={getCourseStatus}
        className="p-column-filter"
        style={{
          width: '100%',
          height: '30px',
          alignItems: 'center',
        }}
      />
    );
  };

  const yearFilterElement = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={years}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="name"
        optionValue="year"
        placeholder="בחר שנה"
        className="p-column-filter"
        style={{
          width: '100%',
          height: '30px',
          alignItems: 'center',
        }}
      />
    );
  };

  const yearOptions = [
    { label: 'שנה א׳', value: 'year1' },
    { label: 'שנה ב׳', value: 'year2' },
    { label: 'שנה ג׳', value: 'year3' },
  ];

  const [courseIdLoading, setCourseIdLoading] = useState<number | null>(null);

  const handleExpandedRows = () => {
    setExpandedRows([]);
  };

  const onRowToggle = (e: { data: TableRowType[] }) => {
    setExpandedRows(e.data);
  };

  const onSelectionChange = (
    e: DataTableSelectionMultipleChangeEvent<TableRowType[]>,
  ) => {
    setSelection(e.value ?? []);
  };

  const changeCourseYear = async (courseId: number, v) => {
    if (!programData?.metadata) return;

    await updateCourseYearOperation(
      programData.metadata.branchId,
      programData.metadata.id,
      courseId,
      v.year,
      setCourseIdLoading,
      handleExpandedRows,
    );
  };

  const handleStudentDelete = async (courseId: number) => {
    const branchId = globalConfigStore.rootFilter.currentBranches?.id;
    const currentProgramId = globalProgramStore.getCurrentId();

    await deleteProgramCourseOperation(
      branchId,
      currentProgramId,
      courseId,
      setCourseIdLoading,
    );
  };

  const handleExportCoursesToExcel = (year: number | null) => {
    const programName = programData?.metadata?.programName || 'תוכנית';
    const yearCourses = allCourses.filter(
      (course) => course.programYear === year,
    );

    // Determine year name
    let yearName = '';
    if (year === null) {
      yearName = 'יתרה ליישום';
    } else {
      yearName = `שנה ${numberToHebrewLetter(year)}'`;
    }

    // Create header row
    const headers = [
      'שם הקורס',
      'סך שעות',
      'אחוז נוכחות חובה',
      'סטטוס',
      'ציון ממוצע',
    ];

    // Helper function to get status text
    const getStatusText = (status: CourseStatus) => {
      switch (status) {
        case CourseStatus.ACTIVE:
          return 'פעיל';
        case CourseStatus.CLOSED:
          return 'נסגר';
        case CourseStatus.FINISHED:
          return 'הסתיים';
        case CourseStatus.INIT:
          return 'אותחל';
        default:
          return '';
      }
    };

    // Create data rows
    const dataRows = yearCourses.map((course) => [
      course.abstractData?.courseName || '',
      course.abstractData?.courseHours?.toString() || '',
      course.abstractData?.attendancePercentage?.toString() || '',
      getStatusText(course.status),
      course.averageGrade !== null && course.averageGrade !== undefined
        ? course.averageGrade.toString()
        : 'טרם הוזנו ציונים',
    ]);

    // Combine headers and data
    const excelData = [headers, ...dataRows];

    // Generate and download
    generateAndDownloadExcelFile(excelData);
  };

  const years = useMemo(() => {
    if (!totalYears) {
      return [];
    }
    return [
      ...Array.from({ length: totalYears }, (_, i) => ({
        name: `שנה ${numberToHebrewLetter(i)}'`,
        year: i,
      })),
      { name: 'ללא פילטור', year: null },
    ];
  }, [totalYears]);

  const startYear = 2025;

  const getHebrewYearForProgram = (year: number | null) => {
    if (year === null) return '';
    return getHebrewYear(startYear + year);
  };

  const getUnassignedCoursesCount = () => {
    return programData?.details?.unAssignCourses?.length ?? 0;
  };

  const getCoursesInYear = (year: number | null) => {
    return allCourses.filter((item) => item.programYear === year).length;
  };

  const getGroupHeaderContent = (data: TableRowType) => {
    if (data.programYear === null) {
      return `יתרה ליישום (${getUnassignedCoursesCount()})`;
    }
    return `שנה  ${numberToHebrewLetter(data.programYear)}' ${getHebrewYearForProgram(data.programYear)} (${getCoursesInYear(data.programYear)} קורסים)`;
  };

  const getYearLabel = (programYear: number | null | undefined) => {
    if (programYear === null || programYear === undefined) {
      return 'שנה שנה';
    }
    const yearData = years.find((y) => Number(y.year) === programYear);
    return yearData?.name ?? 'שנה שנה';
  };

  return (
    <div>
      <ExportButton
        onClick={() => {
          // TODO
        }}
      />
      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        selectionMode="multiple"
        selection={selection}
        onSelectionChange={onSelectionChange}
        value={coursesWithVirtualGroups}
        onRowClick={(event: DataTableRowClickEvent) => {
          onRowSelected(event.index, event.data as CourseMetadataType);
        }}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
        filterDisplay="row"
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        rowGroupMode="subheader"
        groupRowsBy="programYear"
        expandableRowGroups
        expandedRows={expandedRows}
        onRowToggle={onRowToggle}
        rowGroupHeaderTemplate={(data: TableRowType) => {
          const isVirtual = 'isVirtual' in data && data.isVirtual;
          const hasData =
            data.programYear === null
              ? getUnassignedCoursesCount() > 0
              : getCoursesInYear(data.programYear) > 0;

          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <div
                onClick={() => {
                  if (!isVirtual) {
                    const isExpanded = expandedRows.some(
                      (row) => row.programYear === data.programYear,
                    );
                    const newExpandedRows = isExpanded
                      ? expandedRows.filter(
                          (row) => row.programYear !== data.programYear,
                        )
                      : [...expandedRows, data];
                    setExpandedRows(newExpandedRows);
                  }
                }}
                style={{
                  display: 'flex',
                  gap: '4px',
                  fontWeight: 'bold',
                  alignItems: 'center',
                }}
              >
                <i className="pi pi-chevron-down" />
                {getGroupHeaderContent(data)}
                {data.programYear !== null &&
                  globalSettingsBranches.workingTimeManagement
                    ?.currentWorkingYear ===
                    data.programYear +
                      programData.metadata.programStartYear && (
                    <Tag
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '90px',
                      }}
                    >
                      שנה נוכחית
                    </Tag>
                  )}
              </div>
              {!isVirtual && hasData && (
                <Button
                  style={{
                    height: '30px',
                    width: '30px',
                    backgroundColor: '#10b981',
                    borderColor: '#10b981',
                  }}
                  icon="pi pi-file-excel"
                  tooltip="ייצוא לאקסל"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportCoursesToExcel(data.programYear);
                  }}
                />
              )}
            </div>
          );
        }}
      >
        <Column style={{ textAlign: 'right', width: '100px' }} />

        <Column
          style={{ textAlign: 'right' }}
          field="abstractData.courseName"
          header="שם הקורס"
          {...createFilterProps('שם הקורס')}
        />

        <Column
          field="abstractData.courseHours"
          header="מכסת שעות מקורית"
          style={{ textAlign: 'right' }}
          {...createFilterProps('סך שעות מקורית')}
        />

        <Column
          field="capacityHours"
          header="מכסת שעות עדכנית"
          style={{ textAlign: 'right' }}
          {...createFilterProps('מכסת שעות עדכנית')}
        />
        <Column
          field="abstractData.attendancePercentage"
          header="אחוז נוכחות חובה"
          style={{ textAlign: 'right' }}
          {...createFilterProps('אחוז נוכחות')}
        />

        <Column
          field="status"
          header="סטטוס"
          style={{ textAlign: 'right', width: '200px' }}
          {...createFilterProps('סטטוס')}
          body={(rowData: CourseMetadataType) =>
            getCourseStatus(rowData.status)
          }
          showFilterMenu={false}
          filterElement={statusFilterElement}
          filterMatchMode={FilterMatchMode.EQUALS}
        />

        <Column
          field="averageGrade"
          header="ציון ממוצע"
          style={{ textAlign: 'right', width: '150px' }}
          body={(rowData: CourseMetadataType) => (
            <div style={{ textAlign: 'right' }}>
              {rowData.averageGrade !== null &&
              rowData.averageGrade !== undefined
                ? rowData.averageGrade.toString()
                : 'טרם הוזנו ציונים'}
            </div>
          )}
        />

        <Column
          field="programYear"
          header="שנה"
          style={{ textAlign: 'right', width: '200px' }}
          {...createFilterProps('שנה')}
          body={(rowData: CourseMetadataType) => (
            <div>
              <Dropdown
                value={rowData.programYear}
                onChange={(e) => changeCourseYear(rowData.id, e.value)}
                loading={false}
                options={years}
                optionLabel="name"
                placeholder="Select a City"
                style={{ height: '35px', alignItems: 'center', border: 'none' }}
                valueTemplate={
                  <Button
                    style={{ display: 'flex', gap: '4px' }}
                    label={getYearLabel(rowData.programYear)}
                    icon="pi pi-sync"
                    text
                  />
                }
                className="w-full md:w-14rem"
              />
            </div>
          )}
          showFilterMenu={false}
          filterElement={yearFilterElement}
          filterMatchMode={FilterMatchMode.EQUALS}
          filterFunction={(value, filter) => {
            if (filter === null || filter === undefined) {
              return value === null || value === undefined;
            }
            return value === filter;
          }}
        />

        {/*<Column
          style={{ textAlign: 'right' }}
          field="נשלח ע״י"
          header=""
          body={(rowData: CourseMetadataType) => (
            <Button
              id={`delete-button`}
              icon="pi pi-trash"
              text
              severity="danger"
              tooltip={'מחיקת הרצאה'}
              loading={courseIdLoading === rowData.id}
              disabled={courseIdLoading !== null}
              onClick={async (e) => {
                e.stopPropagation(); // Stop the event from bubbling up

                await handleStudentDelete(rowData.id);
              }}
            />
          )}
        />*/}
      </DataTable>
    </div>
  );
});

export default ProgramCoursesTab;
